import { DetectionFilterInput } from "../../redux/detections/detection-types";
import {
  RxList,
  TxSelection,
  RxSelection,
  SelectionInterval,
  FilterState,
} from "./detection-types";
import { TimeRangeTs } from "./detection-types";
import { BIN_WIDTHS, IDEAL_BINS } from "./detection-consts";
import { Duration } from "./duration";
import { max, min } from "lodash";
import { parseDateTime } from "../../helpers/time"; // Adjust path if needed
import { Animal } from "../../redux/animals/animals-types";

export function detectionFiltersToTimeRangeTS(filters: DetectionFilterInput): TimeRangeTs {
  return {
    min: filters.includeStartTime?.valueOf() || 0,
    max: filters.includeEndTime?.valueOf() || 0,
  };
}

export function getBinInfo(nSeconds: number, idealBins: number = IDEAL_BINS): Duration {
  const secondsPerBin = nSeconds / idealBins;

  const differences = BIN_WIDTHS.map(binWidth => ({
    binWidth,
    difference: Math.abs(binWidth.getValueSeconds() - secondsPerBin),
  }));

  return differences.reduce((accumulator, currentValue) =>
    currentValue.difference < accumulator.difference ? currentValue : accumulator
  ).binWidth;
}

export function buildDetectionFilter({
  filterState,
  rxs,
  selectionInterval,
}: {
  filterState: FilterState;
  rxs?: RxList;
  selectionInterval?: SelectionInterval;
}): DetectionFilterInput {
  let includeReceiverSerials: string[] | undefined = undefined;
  if (filterState.selectedRxs.length > 0) {
    includeReceiverSerials = filterState.selectedRxs.map(({ serial }) => serial);
  } else if (rxs) {
    // Unlike transmitters it's important we include the selectable rxs
    // because they could be scoped only to the study if a study is selected
    includeReceiverSerials = rxs.map(rx => rx.serial);
  }

  let includeTransmitterIDs: string[] | undefined = undefined;
  if (filterState.selectedTxs.length > 0) {
    includeTransmitterIDs = filterState.selectedTxs.map(({ displayId }) => displayId);
  }

  const includeStartTime = filterState.start ? parseDateTime(filterState.start) : undefined;
  const includeEndTime = filterState.end ? parseDateTime(filterState.end) : undefined;

  let start = includeStartTime?.valueOf() || 0;
  let end = includeEndTime?.valueOf() || 0;
  if (selectionInterval && selectionInterval.length === 2) {
    start = max([selectionInterval[0], start]) as number;
    end = min([selectionInterval[1], end]) as number;
  }

  return {
    includeReceiverSerials,
    includeTransmitterIDs,
    includeStartTime: start > 0 ? new Date(start) : undefined,
    includeEndTime: end > 0 ? new Date(end) : undefined,
  };
}

export function buildSelectableTxs(rxs: RxList, selectedRxs: RxSelection[]): TxSelection[] {
  const selRxSerials = selectedRxs.length ? selectedRxs.map(sr => sr.serial) : [];
  const rxTemp: RxList = rxs.filter(r =>
    selRxSerials.length ? selRxSerials.includes(r.serial) : true
  );
  const txIdCounts: { [displayId: string]: number } = {};
  const txSerials: { [displayId: string]: string[] } = {};
  rxTemp.forEach(r => {
    r.idCounts.forEach(({ displayId, count }) => {
      if (txIdCounts[displayId] === undefined) {
        txIdCounts[displayId] = 0;
      }
      if (txSerials[displayId] === undefined) {
        txSerials[displayId] = [];
      }
      txSerials[displayId].push(r.rxName);
      txIdCounts[displayId] += count;
    });
  });
  const selectableTxs: TxSelection[] = Object.keys(txIdCounts).map(displayId => ({
    displayId,
    serials: txSerials[displayId],
    count: txIdCounts[displayId],
  }));
  return selectableTxs.sort((t1, t2) => t2.count - t1.count);
}

/**
 * Get animal names for animals that have tagging events.
 * Returns the animal name or "Unnamed-{id}" if no name is set.
 */
export function getAnimalNamesWithTaggingEvents(animals: Animal[]): string[] {
  return animals
    .filter(animal => animal.events.some(event => event.__typename === "AnimalEventTagging"))
    .map(animal => animal.name || `Unnamed-${animal.id}`);
}

